import { useCallback } from "react";
import { MenuItem } from "@progress/kendo-react-layout"
import { GridCellProps } from "@progress/kendo-react-grid";
import { routes } from "../../app/routes";
import { useSelector } from "react-redux";
import { colorPrimary, RootState } from "../../app";
import { ServiceTypeModel } from "../../data/entities";
import { PermissionDefinitions } from "../../security";
import { ServiceTypeService } from "../../services";
import { usePageActions } from "../";
import { useCRUDHandlers,
  useGridActions,
  usePermission,
  useSetPageTitle,
  useSharedState,
  useTenantFeatures } from "../../hooks";
import { AddServiceTypeModal } from "./components";
import { TitlelessCard,
  ODataGrid,
  Breakpoints,
  ConfirmationModal,
  IconAndText,
  IconDefinitions,
  ResponsiveGridColumn,
  Button,
  Icon } from "../../components"

export function ServiceTypesIndexPage() {

  const gridData = useSharedState<Array<ServiceTypeModel>>([]);
  const serviceTypeService = new ServiceTypeService();

  const { onClick, onSelect, showAddState, showDeleteState, deleteItemState, showDelete } = useGridActions<ServiceTypeModel>(routes.serviceType.details);
  const { gridRef, getItems, addItem, deleteItem } = useCRUDHandlers<ServiceTypeModel>(serviceTypeService, "Service Type", showAddState);

  const tenantId = useSelector((state: RootState) => state.authentication.tenantId);
 
  const { manageServiceTypes } = useTenantFeatures();
  const canAddServiceType = usePermission(PermissionDefinitions.serviceType.create(tenantId!)) && manageServiceTypes;
  const canDeleteServiceType = usePermission(PermissionDefinitions.serviceType.delete(tenantId!)) && manageServiceTypes;

  useSetPageTitle("Service Types", []);
  usePageActions(
    canAddServiceType && (
      <Button onClick={() => {showAddState.setter(true);}}>
        <IconAndText iconName={IconDefinitions.add} text="Add" />
      </Button>
    ),
    [canAddServiceType]
  );

  const colorSwatchCell = (props: GridCellProps) => {
    const colorHex = props.dataItem[props.field || colorPrimary];
    return (
      <td style={{ textAlign: "center" }}>
        <div
          style={{
            backgroundColor: colorHex,
            width: "1.5rem",
            height: "1.5rem",
            borderRadius: "25%",
            display: "inline-block",
          }}
          title={colorHex}
        ></div>
      </td>
    );
  };

  const commandsCell = useCallback(
    (props: GridCellProps) => (
      <td className="text-center">
        <Icon
          iconName={IconDefinitions.delete}
          className="text-danger"
          onClick={() => showDelete(props.dataItem)}
        />
      </td>
    ),
    [showDelete]
  );

  return (
    <TitlelessCard bodyClassName="p-0">
      <ODataGrid
        ref={gridRef}
        getData={(oDataQuery) =>{ 
          return getItems(`${oDataQuery}`)
        }}
        dataState={gridData}
        onRowClick={onClick}
        onRowMenuSelect={onSelect}
        sort={[{ field: "name", dir: "asc"}]}
        scrollable="scrollable"
        pageSize={10}
      >
        <ResponsiveGridColumn
          title="Name"
          field="name"
          sortable={true}
          width={"auto"}
        />
        <ResponsiveGridColumn
          title="Description"
          field="description"
          sortable={true}
          width={"auto"}
          breakpoint={Breakpoints.md}
        />
        <ResponsiveGridColumn
          title=" "
          field="color"
          sortable={false}
          filterable={false}
          headerCell={() => null}
          cell={colorSwatchCell}
          width={50}
          breakpoint={Breakpoints.lg}
        />
        {canDeleteServiceType === true && (
          <ResponsiveGridColumn
            className="column-url"
            field="url"
            title=" "
            sortable={false}
            filterable={false}
            headerCell={() => null}
            cell={commandsCell}
            width={50}
          />
        )}
        <MenuItem
          text="Open"
          data={{ action: "open" }}
          icon="hyperlink-open"
        />
        <MenuItem
          text="Open Tab"
          data={{ action: "opentab" }}
          icon="windows"
        />
        {canDeleteServiceType && (
          <MenuItem
            text="Delete"
            data={{ action: "delete" }}
            icon={IconDefinitions.delete}
          />
        )}
      </ODataGrid>
      <AddServiceTypeModal
        showState={showAddState}
        showAddState={showAddState}
        onSubmit={addItem}
      />
      <ConfirmationModal
        showState={showDeleteState}
        title={`Delete ${deleteItemState.value?.name}`}
        message={`You are about to delete this service type, it cannot be undone. Are you sure you want to delete ${deleteItemState.value.name}?`}
        confirmText="Delete"
        onConfirm={() =>{deleteItem(deleteItemState.value)}}
      />
    </TitlelessCard>
  )
}

export default ServiceTypesIndexPage
